/**
 * Lectures Page - Frontend
 * @description Página 3: Operation Lectures - Shows lectures for a specific operation
 * Includes export functionality and operation details
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Download, FileSpreadsheet, Activity, RefreshCw } from 'lucide-react';

// Components
import { LecturesTable } from '@/components/features/operations';
import { LoadingOverlay } from '@/components/ui/LoadingOverlay';

// Services and mappers
import { deviceService, operationService, dashboardService } from '@/services/api';
import { formatDateAsYearMonthDayHourMinute } from '@/utils/formatters/dateFormatter';

// Hooks and contexts
import { useToast } from '@/contexts/ToastContext';
import { useExcelExport } from '@/hooks/useExcelExport';
import { useAuth } from '@/contexts/AuthContext';

// Types
import { LecturesTableData, UILectureEvent } from '@/types/operational-ui.types';
import { Device } from '@/types/device.types';
import { OperationResponse } from '@/types/operation.types';

export const OperationLecturesPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { user } = useAuth();
  const { deviceId, operationId } = useParams<{ deviceId: string; operationId: string }>();
  const { exportLectures, generateFileName, isExporting } = useExcelExport({ showToasts: false });

  const [device, setDevice] = useState<Device | null>(null);
  const [operation, setOperation] = useState<OperationResponse | null>(null);
  const [lectures, setLectures] = useState<LecturesTableData[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isCheckingSecurity, setIsCheckingSecurity] = useState(true);

  // Definir deviceName temprano para evitar errores de referencia
  const deviceName = device?.vehicle?.plate || device?.serialNumber || deviceId;

  // Security validation function
  const validateDeviceAccess = useCallback(async () => {
    if (!user?.id || !deviceId) {
      showToast(t('errors.userNotAuthenticated'), 'error');
      navigate(`/operations/device/${deviceId}`);
      return;
    }

    try {
      setIsCheckingSecurity(true);

      // For Administrators, they can access any device
      if (user.role === 'Administrador') {
        setIsCheckingSecurity(false);
        return;
      }

      // For Supervisors and Operarios, check if they have access to this specific device
      if (user.role === 'Supervisor' || user.role === 'Operario') {
        const userDevicesResponse = await deviceService.getByUserId(user.id);

        if (!userDevicesResponse.success || !userDevicesResponse.data) {
          showToast(t('errors.failedToCheckDeviceAccess'), 'error');
          navigate(`/operations/device/${deviceId}`);
          return;
        }

        // Check if the device ID is in the user's associated devices
        const hasAccess = userDevicesResponse.data.some((device) => device.id === deviceId);

        if (!hasAccess) {
          showToast(t('errors.youDontHaveAccessToThisDevice'), 'error');
          navigate(`/operations/device/${deviceId}`);
          return;
        }
      }

      setIsCheckingSecurity(false);
    } catch (error) {
      console.error('Error validating device access:', error);
      showToast(t('errors.securityValidationFailed'), 'error');
      navigate(`/operations/device/${deviceId}`);
    }
  }, [user, deviceId, t, showToast, navigate]);

  const mapDashboardLectureToTableData = useCallback(
    (dashboardLecture: any): LecturesTableData => ({
      id: dashboardLecture.id,
      lectureIdentifier: dashboardLecture.lectureIdentifier,
      deviceId: dashboardLecture.deviceId,
      operationId: dashboardLecture.operationId,
      timestamp: dashboardLecture.timestamp,
      tz: dashboardLecture.tz,
      coordinates: dashboardLecture.coordinates,
      eventType: dashboardLecture.eventType as UILectureEvent,
      inclinationAngle: dashboardLecture.inclinationAngle,
      angleLimits: dashboardLecture.angleLimits,
      sequence: dashboardLecture.sequence,
      timestampFormatted: formatDateAsYearMonthDayHourMinute(dashboardLecture.timestamp, dashboardLecture.tz),
      coordinatesFormatted: `${dashboardLecture.coordinates.latitude.toFixed(6)}, ${dashboardLecture.coordinates.longitude.toFixed(6)}`,
      inclinationAngle_lateral: dashboardLecture.inclinationAngle.lateral,
      inclinationAngle_longitudinal: dashboardLecture.inclinationAngle.longitudinal,
      angleLimits_lateral: dashboardLecture.angleLimits.lateral,
      angleLimits_longitudinal: dashboardLecture.angleLimits.longitudinal,
      lectureId: dashboardLecture.id,
    }),
    []
  );

  const loadData = useCallback(async () => {
    if (!deviceId || !operationId) return;

    try {
      setLoading(true);

      // Load device, operation, and first lectures page
      const [deviceResponse, operationResponse, lecturesPageResponse] = await Promise.all([
        deviceService.getById(deviceId), // TODO: Esto deberia llegar como info del componente padre => evitar una llamada extra
        operationService.getById(operationId), // TODO: Esto deberia llegar como info del componente padre => evitar una llamada extra
        dashboardService.getLecturesByOperationIdPage(operationId, { limit: 300 }),
      ]);

      // Handle device response
      if (!deviceResponse.success) {
        throw new Error(deviceResponse.error.message || 'Device not found');
      }
      if (!deviceResponse.data) {
        throw new Error('Device not found');
      }
      setDevice(deviceResponse.data);

      // Handle operation response
      if (!operationResponse.success) {
        throw new Error(operationResponse.error.message || 'Operation not found');
      }
      if (!operationResponse.data) {
        throw new Error('Operation not found');
      }
      setOperation(operationResponse.data);

      if (!lecturesPageResponse.success) {
        console.warn('No lectures found for operation:', operationId);
        setLectures([]);
        setHasMore(false);
        setNextCursor(null);
        return;
      }

      const tableData = lecturesPageResponse.data.items.map(mapDashboardLectureToTableData);
      setLectures(tableData);
      setHasMore(lecturesPageResponse.data.hasMore);
      setNextCursor(lecturesPageResponse.data.nextCursor);
    } catch (error) {
      console.error('Error loading lectures:', error);
      showToast(t('pages.lectures.loadError'), 'error');

      // If not found, redirect back
      if (error instanceof Error && error.message.includes('not found')) {
        navigate(`/operations/device/${deviceId}`);
      }
    } finally {
      setLoading(false);
    }
  }, [deviceId, operationId, navigate, showToast, t, mapDashboardLectureToTableData]);

  const handleLoadMore = useCallback(async () => {
    if (!operationId || !nextCursor || isLoadingMore || !hasMore) return;
    try {
      setIsLoadingMore(true);
      const response = await dashboardService.getLecturesByOperationIdPage(operationId, {
        limit: 300,
        cursor: nextCursor,
      });
      if (!response.success) return;
      const nextRows = response.data.items.map(mapDashboardLectureToTableData);
      setLectures((prev) => [...prev, ...nextRows]);
      setHasMore(response.data.hasMore);
      setNextCursor(response.data.nextCursor);
    } finally {
      setIsLoadingMore(false);
    }
  }, [operationId, nextCursor, isLoadingMore, hasMore, mapDashboardLectureToTableData]);

  // Security validation effect
  useEffect(() => {
    if (user?.id && deviceId) {
      validateDeviceAccess();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, deviceId]); // Remove validateDeviceAccess from dependencies to prevent loop

  useEffect(() => {
    // Only load data if security check passed
    if (!isCheckingSecurity) {
      loadData();
    }
  }, [isCheckingSecurity, loadData]);

  // Funcion para refrescar los datos
  const handleRefresh = () => {
    loadData();
  };

  const downloadExportBlob = useCallback((blob: Blob, fileName: string) => {
    const url = globalThis.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    globalThis.URL.revokeObjectURL(url);
  }, []);

  const handleExportCSV = useCallback(() => {
    if (!operationId) return;
    dashboardService
      .exportLecturesByOperation(operationId, 'csv')
      .then((blob) => {
        downloadExportBlob(
          blob,
          `Lecturas_Operacion_${operationId}_${new Date().toISOString().split('T')[0]}.csv`
        );
        showToast(t('common.exportSuccessful'), 'success');
      })
      .catch(() => showToast(t('common.exportError'), 'error'));
  }, [operationId, downloadExportBlob, showToast, t]);

  // Función para exportar a XLSX
  const handleExportXLSX = useCallback(async () => {
    try {
      if (!operationId) return;
      const blob = await dashboardService.exportLecturesByOperation(operationId, 'xlsx');
      const fileName = generateFileName('lecturas_operacion', operationId || 'operation');
      downloadExportBlob(blob, `${fileName}.xlsx`);
      showToast(t('common.exportSuccessful'), 'success');
    } catch (error) {
      showToast(
        `❌ Error al exportar: ${error instanceof Error ? error.message : 'Error desconocido'}`,
        'error'
      );
    }
  }, [operationId, generateFileName, showToast, t, downloadExportBlob]);

  const handleBackToOperations = () => {
    navigate(`/operations/device/${deviceId}`);
  };
  const minuteStringTrasnlated = t('common.minutes');
  // We calculate the duration in minutes
  const duration =
    operation?.endTimestamp && operation?.startTimestamp
      ? Math.round(
          (new Date(operation.endTimestamp).getTime() -
            new Date(operation.startTimestamp).getTime()) /
            (1000 * 60)
        )
      : undefined;
  const operationInfo = operation
    ? {
        identifier: operation.operationIdentifier,
        vehicle: deviceName,
        performance: operation.performance,
        startDate: operation.startTimestamp
          ? new Date(operation.startTimestamp).toLocaleDateString()
          : '',
        endDate: operation.endTimestamp
          ? new Date(operation.endTimestamp).toLocaleDateString()
          : '',
        duration: duration ? `${duration} ${minuteStringTrasnlated}` : 'N/A',
      }
    : undefined;

  // Show loading while checking security
  if (isCheckingSecurity) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Loading Overlay - Reusable Component */}
      <LoadingOverlay
        loading={loading}
        title={t('common.loading')}
        message={t('pages.lectures.loadingLectures')}
      />

      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-200">
        <div className="flex items-center space-x-4">
          {/*
          <button
            onClick={handleBackToOperations}
            className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('common.back')}
          </button>
          */}

          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {t('pages.lectures.operationDetails')}
            </h2>
            <div className="grid grid-cols-2 gap-4 text-sm">
              {/*
              <div>              
                <span className="font-medium text-gray-700">
                  {t('pages.lectures.operationId')}:
                </span>
                <span className="ml-2 text-gray-900">
                  {operationInfo?.identifier || operationId}
                </span>
              </div>
              */}
              <div className="col-span-2">
                <span className="font-medium text-gray-700">
                  {t('pages.lectures.deviceSerialNumber')}
                </span>
                <span className="ml-2 text-gray-900">{operationInfo?.vehicle}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">{t('pages.lectures.duration')}:</span>
                <span className="ml-2 text-gray-900">{operationInfo?.duration}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">
                  {t('pages.lectures.totalLectures')}:
                </span>
                <span className="ml-2 text-gray-900">{lectures.length}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Export Buttons */}
        <div className="flex gap-3">
          {handleRefresh && (
            <button
              onClick={handleRefresh}
              className="bg-brand-orange text-brand-white px-4 py-2 rounded-md hover:bg-opacity-90 transition-colors duration-200 flex items-center gap-2"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              {t('common.refresh')}
            </button>
          )}
          <button
            onClick={handleExportCSV}
            disabled={lectures.length === 0}
            className="bg-brand-orange text-brand-white px-4 py-2 rounded-md hover:bg-opacity-90 transition-colors duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="h-4 w-4 mr-2" />
            {t('common.exportCSV')}
          </button>

          <button
            onClick={handleExportXLSX}
            disabled={lectures.length === 0 || isExporting}
            className="bg-brand-orange text-brand-white px-4 py-2 rounded-md hover:bg-opacity-90 transition-colors duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isExporting ? (
              <>
                <svg className="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Exportando...
              </>
            ) : (
              <>
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                {t('common.exportXLSX')}
              </>
            )}
          </button>
        </div>
      </div>

      {/* Operation Summary - Hidden temporarily */}

      {/* Lectures Table */}
      <LecturesTable
        lectures={lectures}
        loading={false}
        showSequence={true} // Show sequence column for device-level lectures
        showOperationId={true} // Show operation id column for device-level lectures
        hasMore={hasMore}
        isLoadingMore={isLoadingMore}
        onLoadMore={handleLoadMore}
      />

      {/* Navigation Actions - Hidden temporarily */}

      {/* Empty State */}
      {!loading && lectures.length === 0 && (
        <div className="text-center py-12">
          <Activity className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            {t('pages.lectures.empty.title')}
          </h3>
          <p className="mt-1 text-sm text-gray-500">{t('pages.lectures.empty.description')}</p>
          <div className="mt-6">
            <button
              type="button"
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              onClick={handleBackToOperations}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('pages.lectures.empty.backToOperations')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
